/* eslint-disable @typescript-eslint/naming-convention */
import React, { HTMLAttributes } from "react";
import styled from "styled-components";
import { style } from "./style";

const WIDTH = style.vars.height.button;
const HEIGHT = "12px";
const KNOB_SIZE = "24px";
const BORDER_RADIUS = "30px";

const BORDER_COLOR = style.colors.grey.dark;
const BACKGROUND_COLOR = style.colors.grey.lightest;

const BORDER_COLOR_CHECKED = style.colors.blue.dark;
const RAIL_BACKGROUND_COLOR_CHECKED = style.colors.blue.dark;
const BACKGROUND_COLOR_CHECKED = style.colors.blue.lightest;

const SwitchToggle = styled.span`
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  width: ${WIDTH};
  height: ${HEIGHT};
  margin: 0 ${style.vars.whitespace.small};
  &:before,
  &:after {
    position: absolute;
    content: "";
    display: block;
    transition: background ${style.vars.easeInOutCubic} ${style.vars.transitionSpeed.base};
  }

  /* Switch Rail */
  &:before {
    height: ${HEIGHT};
    width: ${WIDTH};
    background-color: ${BACKGROUND_COLOR};
    border: 1px solid ${BORDER_COLOR};
    border-radius: ${BORDER_RADIUS};
    cursor: pointer;
    transition: background ${style.vars.easeInOutCubic} ${style.vars.transitionSpeed.base};
  }

  /* Switch Knob */
  &:after {
    display: block;
    content: "";
    width: ${KNOB_SIZE};
    height: ${KNOB_SIZE};
    border-radius: 100%;
    background-color: ${style.colors.white};
    border: 1px solid ${BORDER_COLOR};
    transition: all ${style.vars.easeInOutCubic} ${style.vars.transitionSpeed.base};
  }
`;

const SwitchLabel = styled.label`
  display: flex;
  align-items: center;
  margin: ${style.vars.space["2"]} 0;

  input[type="checkbox"]:focus + ${SwitchToggle} {
    outline: ${style.colors.grey.dark} dotted ${style.vars.borderWidth.base};
    outline-offset: 2px;
  }

  input[type="checkbox"]:checked + ${SwitchToggle} {
    &:before {
      background-color: ${RAIL_BACKGROUND_COLOR_CHECKED};
      border: 1px solid ${BORDER_COLOR_CHECKED};
    }

    &:after {
      margin-left: calc(100% - ${KNOB_SIZE} + 3px);
      transition: all ${style.vars.easeInOutCubic} ${style.vars.transitionSpeed.base};
      background-color: ${BACKGROUND_COLOR_CHECKED};
      border: 1px solid ${BORDER_COLOR_CHECKED};
    }
  }

  input[type="checkbox"]:disabled + ${SwitchToggle} {
    opacity: ${style.vars.disabledOpacity};
    cursor: not-allowed;
  }
`;

const SwitchInput = styled.input`
  position: absolute;
  margin: 0;
  opacity: 0;
`;

export type SwitchProps = {
  name: string;
  label?: string;
  value?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  inputProps?: HTMLAttributes<HTMLInputElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
};

export const Switch = React.forwardRef<HTMLInputElement, SwitchProps>((props, ref) => {
  const { name, checked, defaultChecked, disabled, value, label, onChange, inputProps = {} } = props;

  return (
    <SwitchLabel tabIndex={-1} htmlFor={name}>
      {label}
      <SwitchInput
        ref={ref}
        onChange={onChange}
        type="checkbox"
        role="switch"
        id={name}
        name={name}
        value={value}
        disabled={disabled}
        checked={checked}
        aria-checked={checked}
        defaultChecked={defaultChecked}
        {...inputProps}
      />
      <SwitchToggle />
    </SwitchLabel>
  );
});
